import { memo } from "react"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import {
  MemberAvatars,
  type MemberInfo,
} from "@/features/navigation/MemberAvatars"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TaskRowAvatarsProps {
  realmId: string
  className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Compact member avatars for task rows — displays shared realm members.
 *
 * Matches v1's `<Avatars>` (GetAvatars) pattern: per-realmId query via
 * `useLiveQueryImmediate` on Dexie Cloud's built-in `members` table.
 * Shows max 2 avatars in compact row context.
 */
export const TaskRowAvatars = memo(function TaskRowAvatars({
  realmId,
  className,
}: TaskRowAvatarsProps) {
  const [members] = useLiveQueryImmediate(
    async () => {
      if (!realmId) return []
      try {
        const membersTable = db.table("members")
        const currentUser = db.cloud.currentUser.value
        const currentEmail = currentUser?.email ?? ""

        const allMembers = await membersTable
          .where("realmId")
          .equals(realmId)
          .toArray()

        const result: MemberInfo[] = []
        for (const m of allMembers as Array<{
          realmId: string
          email?: string
          owner?: string
          accepted?: boolean
        }>) {
          const email = m.email ?? m.owner ?? ""
          if (!email || email === currentEmail) continue
          if (result.some((x) => x.email === email)) continue
          result.push({ email, pending: m.accepted === false })
        }
        return result
      } catch {
        return []
      }
    },
    ["task-avatars", realmId],
    [],
  )

  if (!members || members.length === 0) return null

  return <MemberAvatars members={members} max={2} className={className} />
})
